import { createFileRoute, Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { trpc } from '@/lib/trpc'
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions'
import { toast } from 'sonner'
import { Badge } from '@deepguide-ai/ui/badge'
import { Button } from '@deepguide-ai/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@deepguide-ai/ui/card'
import {
  Loader2,
  AlertTriangle,
  Building2,
  Clock,
  Link2,
} from 'lucide-react'
import { EmployeeComplianceSections } from '@/components/compliance/people/EmployeeComplianceSections'
import type {
  EmployeeSelfServicePolicyDTO,
  TrainingRecordDTO,
} from '@deepguide-ai/types'
import type { TrainingCompletionSubmissionInput } from '@/components/compliance/people/EmployeeComplianceSections'

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/people/me'
)({
  component: MyEmployeeCompliancePage,
  staticData: { pageHeader: { title: 'My Compliance' } },
})

function MyEmployeeCompliancePage() {
  const { workspaceId } = Route.useParams()
  const { canWorkspaceWrite } = useWorkspacePermissions(workspaceId)
  const queryClient = useQueryClient()

  const dashboardQuery = useQuery(
    trpc.employee.getMyComplianceDashboard.queryOptions()
  )

  const acknowledgeMutation = useMutation(
    trpc.employee.acknowledgeMyPolicy.mutationOptions()
  )
  const submitTrainingMutation = useMutation(
    trpc.employee.submitMyTrainingCompletion.mutationOptions()
  )

  const handleAcknowledge = (policy: EmployeeSelfServicePolicyDTO) => {
    if (!canWorkspaceWrite) return
    acknowledgeMutation.mutate(
      {
        policyDocumentId: policy.policyDocumentId,
        policyDocumentVersionId: policy.policyDocumentVersionId,
        userAgent: navigator.userAgent,
      },
      {
        onSuccess: () => {
          toast.success(`"${policy.title}" acknowledged successfully`)
          queryClient.invalidateQueries({
            queryKey: trpc.employee.getMyComplianceDashboard.queryKey(),
          })
        },
        onError: (error: Error) => {
          toast.error(error.message ?? 'Failed to acknowledge policy')
          queryClient.invalidateQueries({ queryKey: trpc.employee.getMyComplianceDashboard.queryKey() })
        },
      }
    )
  }

  const handleSubmitTraining = (
    record: TrainingRecordDTO,
    input: TrainingCompletionSubmissionInput,
  ) => {
    if (!canWorkspaceWrite) return
    submitTrainingMutation.mutate(
      {
        recordId: record.id,
        completedAt: input.completedAt,
        score: input.score,
        certificateUrl: input.certificateUrl,
        notes: input.notes,
        submissionMethod: input.submissionMethod,
      },
      {
        onSuccess: () => {
          toast.success(`Submitted "${record.program?.name ?? 'training record'}" for admin review`)
          queryClient.invalidateQueries({
            queryKey: trpc.employee.getMyComplianceDashboard.queryKey(),
          })
        },
        onError: (error: Error) => {
          toast.error(error.message ?? 'Failed to submit training completion')
        },
      }
    )
  }

  return (
    <div className='pb-12'>
          {dashboardQuery.isPending ? (
            <div className='flex items-center justify-center py-16 text-muted-foreground'>
              <Loader2 className='mr-2 h-5 w-5 animate-spin' />
              Loading your compliance dashboard…
            </div>
          ) : dashboardQuery.isError ? (
            <Card>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <AlertTriangle className='h-5 w-5 text-destructive' />
                  Unable to load dashboard
                </CardTitle>
                <CardDescription>
                  The employee compliance dashboard could not be loaded.
                </CardDescription>
              </CardHeader>
            </Card>
          ) : dashboardQuery.data?.status === 'not_linked' || !dashboardQuery.data?.dashboard ? (
            <Card>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <Link2 className='h-5 w-5 text-muted-foreground' />
                  Employee profile not linked
                </CardTitle>
                <CardDescription>
                  Your platform account is not linked to an employee record in this workspace.
                </CardDescription>
              </CardHeader>
              <CardContent className='space-y-3'>
                <p className='text-sm text-muted-foreground'>
                  Ask a workspace administrator to link your user account from the People directory.
                </p>
                <Button asChild variant='outline'>
                  <Link
                    to='/workspaces/$workspaceId/people'
                    params={{ workspaceId }}
                  >
                    Open People Directory
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ) : (
            (() => {
              const { employee, pendingPolicies, completedAcknowledgments, trainingRecords, devices, selfServiceAccessStatus } =
                dashboardQuery.data.dashboard

              return (
                <div className='space-y-6'>
                  <div className='rounded-md border bg-card px-6 py-5'>
                    <h1 className='text-xl font-semibold'>
                      {employee.firstName} {employee.lastName}
                    </h1>
                    <p className='mt-1 text-sm text-muted-foreground'>
                      Review your assigned policy acknowledgments, training, and device status.
                    </p>

                    <div className='mt-4 flex flex-wrap gap-4 text-sm'>
                      {employee.title && (
                        <span className='flex items-center gap-1.5 text-muted-foreground'>
                          <Clock className='h-3.5 w-3.5' />
                          {employee.title}
                        </span>
                      )}
                      {employee.department && (
                        <span className='flex items-center gap-1.5 text-muted-foreground'>
                          <Building2 className='h-3.5 w-3.5' />
                          {employee.department}
                        </span>
                      )}
                    </div>

                    <div className=' flex flex-wrap gap-2'>
                      {pendingPolicies.length > 0 ? (
                        <Badge status='amber' type='dot'>
                          {pendingPolicies.length} pending {pendingPolicies.length === 1 ? 'policy' : 'policies'}
                        </Badge>
                      ) : (
                        <Badge status='green' type='dot'>
                          All policies acknowledged
                        </Badge>
                      )}
                      {selfServiceAccessStatus?.hasActiveAccess && (
                        <Badge status='blue' type='dot'>
                          Self-service link active
                        </Badge>
                      )}
                      {devices.some((device) => device.complianceStatus === 'NON_COMPLIANT') && (
                        <Badge status='red' type='dot'>
                          Non-compliant device
                        </Badge>
                      )}
                    </div>
                  </div>

                  <EmployeeComplianceSections
                    pendingPolicies={pendingPolicies}
                    completedAcknowledgments={completedAcknowledgments}
                    trainingRecords={trainingRecords}
                    devices={devices}
                    acknowledgePending={acknowledgeMutation.isPending}
                    onAcknowledge={handleAcknowledge}
                    onSubmitTraining={handleSubmitTraining}
                    submitTrainingPending={submitTrainingMutation.isPending}
                    readOnly={!canWorkspaceWrite}
                    onTrainingRecordsChanged={() => {
                      queryClient.invalidateQueries({
                        queryKey: trpc.employee.getMyComplianceDashboard.queryKey(),
                      })
                    }}
                  />
                </div>
              )
            })()
          )}
    </div>
  )
}
